import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSpring } from 'react-spring';
import { useTranslation } from 'react-i18next';
import { Popover, IconButton, Text, TextButton } from '@oneaudi/unified-web-components';
import { TextInput as TextInputAlpha, TextLink } from '@oneaudi/unified-web-alpha-components';
import { selectInputs, setUserInputs } from '../../../redux/inputsReducer';
import { selectErrors, clearError, setError } from '../../../redux/errorsReducer';
import CancelIcon from '../../../assets/icons/CancelIcon';
import HelperIcon from '../../../assets/icons/HelperIcon';
import {
  DeleteInputIcon,
  ErrorMessage,
  InputContainer,
  InputLabel,
  Note,
  TextInputField,
  TextInputWrapper,
  InputContainerWithIcon,
  OptionalNote,
  PopoverTitle,
  ButtonStyled,
  Content,
  PopoverIconWrapper,
} from './TextInput.styles';
import { Form } from '../../../types/analytics';
import { selectDealerAndVehicleData } from '../../../redux/dealerAndVehicleReducer';
import { INPUT_TYPE, FEATURE_APP_DETAILS } from '../../../constants';
import { convertToPromotionsType } from '../../../functions/convertToPromotionsType';
import CautionIcon from '../../../assets/icons/CautionIcon';
import { reversedPhoneNumberFormatter } from '../../../functions/phoneNumberFormatter';
import { digitalLeadsFormInput } from '../../../utils/analyticsEventListeners';
import { selectForm, setForm } from '../../../redux/formReducer';
import { selectAppConfig } from '../../../redux/appConfigReducer';
import { getVehicleForPromotions } from '../../../functions/getVehicleForPromotions';
import { IS_LOCAL_ENVIRONMENT } from '../../../constants/environment';
import { P } from '../../Common';

interface TextInputProps {
  inputLabel: string;
  inputKey: string;
  inputType: 'text' | 'email' | 'number' | 'date' | 'password' | undefined;
  inputNote?: string;
  helperIcon?: boolean;
  isOptional?: boolean;
  popoverDescription?: string;
  popoverTitle?: string;
}

const TextInput: React.FC<TextInputProps> = ({
  inputLabel,
  inputKey,
  inputType,
  inputNote,
  helperIcon,
  isOptional,
  popoverDescription,
  popoverTitle,
}) => {
  const [focused, setFocused] = useState(false);
  const [isTouched, setIsTouched] = useState(false);
  const [isOpenPopover, setIsOpenedPopover] = useState(false);
  const [hiddenInput, setHiddenInput] = useState(false);

  const targetRef = useRef<HTMLButtonElement>(null);

  const dispatch = useDispatch();
  const { t } = useTranslation();

  const inputs = useSelector(selectInputs);
  const errors = useSelector(selectErrors);

  const inputValue = inputs[inputKey];
  const errorMessage = errors[inputKey];

  const requiredTextInputs = [
    INPUT_TYPE.FIRST_NAME,
    INPUT_TYPE.LAST_NAME,
    INPUT_TYPE.EMAIL,
    INPUT_TYPE.PHONE,
    INPUT_TYPE.ZIP,
    INPUT_TYPE.TRADE_IN_VEHICLE_MAKE,
    INPUT_TYPE.TRADE_IN_VEHICLE_MILEAGE,
    INPUT_TYPE.TRADE_IN_VEHICLE_MODEL,
    INPUT_TYPE.TRADE_IN_VEHICLE_YEAR,
  ];

  const { vehicle, vehicleByCarline, vehicleByAudiCode } = useSelector(selectDealerAndVehicleData);
  const contactForm = useSelector(selectForm);
  const { offers, market } = useSelector(selectAppConfig);
  const promotion = convertToPromotionsType(offers!);
  const isUSMarket = market?.toLowerCase().includes('us');
  const isCAPostalCode = inputKey === INPUT_TYPE.ZIP && !isUSMarket;

  const inputValidation = useCallback(
    (input: string) => {
      // If optional and empty, consider it valid
      if (isOptional && input.trim().length === 0) {
        return true;
      }

      if (inputKey === INPUT_TYPE.EMAIL) {
        const validation = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return input.trim().length > 0 && validation.test(String(input).toLowerCase());
      }
      if (inputKey === INPUT_TYPE.PHONE) {
        const isLengthCorrect = reversedPhoneNumberFormatter(input).length === 10;
        return isLengthCorrect;
      }
      if (inputKey === INPUT_TYPE.ZIP) {
        const usValidation = /^[0-9]{5}(-[0-9]{4})?$/i;
        const caValidation = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/i;
        return (
          input.length > 0 && (isUSMarket ? usValidation.test(input) : caValidation.test(input))
        );
      }

      if (
        inputKey === INPUT_TYPE.FIRST_NAME ||
        inputKey === INPUT_TYPE.LAST_NAME ||
        inputKey === INPUT_TYPE.TRADE_IN_VEHICLE_MAKE ||
        inputKey === INPUT_TYPE.TRADE_IN_VEHICLE_MODEL
      ) {
        return input.trim().length > 0;
      }
      if (inputKey === INPUT_TYPE.TRADE_IN_VEHICLE_YEAR) {
        const isYearValid = /^\d{4}$/.test(input.trim());
        return isYearValid;
      }

      if (inputKey === INPUT_TYPE.TRADE_IN_VEHICLE_MILEAGE) {
        const isMileageValid = /^\d+$/.test(input.trim());
        return isMileageValid;
      }

      if (inputKey === INPUT_TYPE.TRADE_IN_VEHICLE_VIN) {
        const isVinValid = input.trim() === '' || input.trim().length === 17;

        return isVinValid;
      }
      return true;
    },
    [inputKey, isUSMarket, isOptional],
  );

  useEffect(() => {
    if (!isOpenPopover) return undefined;

    const handleScroll = () => {
      setIsOpenedPopover(false);
    };

    const potentialContainers = [
      document.querySelector('[class*="DitigalLeads-styles__Content-sc-"]'),
      document.querySelector('[class*="DitigalLeads-styles__DigitalLeadsModal-sc-"]'),
      document.querySelector('[class*="DitigalLeads-styles__DigitalLeadsWrapper-sc-"]'),
      targetRef.current?.closest('[style*="overflow"]'),
      targetRef.current?.closest('section'),
      document.body,
    ];

    const scrollContainers = potentialContainers.filter(
      (container): container is NonNullable<typeof container> =>
        container !== null && container !== undefined,
    );

    scrollContainers.forEach((container) => {
      container.addEventListener('scroll', handleScroll, { passive: true });
    });

    return () => {
      scrollContainers.forEach((container) => {
        container.removeEventListener('scroll', handleScroll);
      });
    };
  }, [isOpenPopover]);

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setUserInputs({ inputName: e.target.name, inputValue: e.target.value }));
    if (isTouched) {
      if (inputValidation(e.target.value)) {
        dispatch(clearError({ field: inputKey }));
        return;
      }
      // Only set error if field is not optional OR if there's a value (invalid format)
      if (!isOptional || e.target.value.trim().length > 0) {
        dispatch(
          setError({
            field: inputKey,
            error: t(`digitalLeads.inputs.error.${isCAPostalCode ? 'postalCode' : inputKey}`),
          }),
        );
      }
    }
  };

  const deleteInput = () => {
    dispatch(setUserInputs({ inputName: inputKey, inputValue: '' }));
    setFocused(false);
    if (requiredTextInputs.includes(inputKey) && !isOptional) {
      dispatch(
        setError({
          field: inputKey,
          error: t(`digitalLeads.inputs.error.${isCAPostalCode ? 'postalCode' : inputKey}`),
        }),
      );
      return;
    }
    dispatch(clearError({ field: inputKey }));
  };

  const inputAnimation = useSpring({
    to: {
      top: focused || inputValue ? -18 : 8,
      fontSize: focused || inputValue ? '12px' : '16px',
    },
  });

  const handleFocus = () => {
    setFocused(true);
  };

  const handleBlur = useCallback(() => {
    setIsTouched(true);
    if (!inputValue) {
      setFocused(false);
    }

    if (inputKey === 'zipCode') {
      setHiddenInput(true);
    }

    const isZipCode = inputKey === 'zipCode';
    const value = isZipCode ? inputValue : null;

    const inputTouched = {
      inputTitle: inputKey,
      inputValue: value,
      action: 'input',
      inputType: 'field',
    };
    // Use vehicle for VTP, vehicleByCarline/vehicleByAudiCode for NGC/MLP, or offers data for promotions
    const vehicleInView = vehicle || vehicleByCarline || vehicleByAudiCode;
    const vehicleData = vehicleInView || (offers ? getVehicleForPromotions(offers) : undefined);

    if (isZipCode && inputValue.length) {
      const updatedForm: Form = {
        ...contactForm,
        ...{ formData: { ...contactForm.formData, ...{ visitorZipCode: value } } },
      };

      dispatch(setForm(updatedForm));

      if (inputValidation(inputValue)) {
        digitalLeadsFormInput(
          updatedForm,
          vehicleData,
          FEATURE_APP_DETAILS,
          promotion,
          inputTouched,
        );
      }
    }

    if (inputValidation(inputValue) && !isZipCode) {
      digitalLeadsFormInput(contactForm, vehicleData, FEATURE_APP_DETAILS, promotion, inputTouched);
    }

    if (!inputValidation(inputValue)) {
      // Only set error if field is not optional OR if there's a value (invalid format)
      if (!isOptional || inputValue.trim().length > 0) {
        dispatch(
          setError({
            field: inputKey,
            error: t(`digitalLeads.inputs.error.${isCAPostalCode ? 'postalCode' : inputKey}`),
          }),
        );
      }
    }
  }, [
    inputValue,
    inputKey,
    vehicle,
    vehicleByCarline,
    vehicleByAudiCode,
    offers,
    inputValidation,
    contactForm,
    dispatch,
    promotion,
    t,
    isCAPostalCode,
    isOptional,
  ]);

  if (!process.env.IS_LEGACY) {
    return (
      <div data-testid="text-input-wrapper">
        <InputContainer inputKey={inputKey}>
          <TextInputAlpha
            id={inputKey}
            appendix={isOptional ? t('digitalLeads.inputs.optional') : ''}
            margin={['24px', '0', '0px', '0']}
            label={inputLabel}
            value={inputValue}
            hint={inputNote}
            onChange={(e) =>
              handleInput({
                target: { name: inputKey, value: e.target.value },
              } as React.ChangeEvent<HTMLInputElement>)
            }
            onBlur={handleBlur}
            cancelButton={{
              'aria-label': 'On Cancel',
              onClick: deleteInput,
            }}
            type={inputType}
            required={!isOptional}
            errorMessage={errorMessage}
            data-testid={`text-input-${inputKey}`}
          />
          {helperIcon && (
            <PopoverIconWrapper>
              <Popover
                aria-label="status_popover"
                id="status_popover"
                triggerElement={
                  <IconButton
                    variant="tertiary"
                    icon="help-circle"
                    size="sm"
                    onClick={() => setIsOpenedPopover(!isOpenPopover)}
                  />
                }
                open={isOpenPopover}
                content={
                  <Content>
                    <P textStyle="bold">{popoverTitle}</P>
                    <P>{popoverDescription}</P>
                  </Content>
                }
                onClose={() => setIsOpenedPopover(false)}
                withTip
              />
            </PopoverIconWrapper>
          )}
          {hiddenInput && IS_LOCAL_ENVIRONMENT && (
            <>
              <input
                data-testid="hidden-timestamp"
                type="hidden"
                name="lw_hit_timestamp"
                value="||1769013357454||"
              />
              <input
                data-testid="hidden-ga"
                type="hidden"
                name="_ga"
                value="||GA1.1.1387662127.1769004481||"
              />
            </>
          )}
        </InputContainer>
        {!!errorMessage && (
          <ErrorMessage data-testid={`text-input-error-${inputKey}`}>
            <CautionIcon />
            {errorMessage}
          </ErrorMessage>
        )}
      </div>
    );
  }

  return (
    <TextInputWrapper inputKey={inputKey} data-testid="text-input-wrapper">
      <InputContainerWithIcon>
        <InputContainer inputKey={inputKey}>
          <InputLabel
            style={inputAnimation}
            $isError={!!errorMessage}
            data-testid="text-input-label"
          >
            {inputLabel}
            {isOptional && (
              <OptionalNote data-testid={`text-input-${inputKey}-label-optional`}>
                (optional)
              </OptionalNote>
            )}
          </InputLabel>
          <TextInputField
            name={inputKey}
            onChange={handleInput}
            value={inputValue}
            onFocus={handleFocus}
            onBlur={handleBlur}
            type={inputType}
            $isError={!!errorMessage}
            data-testid={`text-input-${inputKey}`}
          />
          {inputValue && (
            <DeleteInputIcon
              id={inputKey}
              type="button"
              aria-label="test"
              onClick={deleteInput}
              data-testid="text-input-remove-input"
            >
              <CancelIcon />
            </DeleteInputIcon>
          )}
          {hiddenInput && IS_LOCAL_ENVIRONMENT && (
            <>
              <input
                data-testid="hidden-timestamp"
                type="hidden"
                name="lw_hit_timestamp"
                value="||1769013357454||"
              />
              <input
                data-testid="hidden-ga"
                type="hidden"
                name="_ga"
                value="||GA1.1.1387662127.1769004481||"
              />
            </>
          )}
        </InputContainer>
        {helperIcon && (
          <Popover
            aria-label="Popover title"
            id="popover_title"
            triggerElement={
              <ButtonStyled
                ref={targetRef}
                variant="tertiary"
                icon="help-circle"
                data-testid={`help-circle-${inputKey}`}
                size="sm"
                onClick={() => {
                  setIsOpenedPopover(!isOpenPopover);
                }}
              />
            }
            open={isOpenPopover}
            content={
              <Content>
                <PopoverTitle>{popoverTitle}</PopoverTitle>
                <Text>{popoverDescription}</Text>
              </Content>
            }
            onClose={() => {
              setIsOpenedPopover(false);
            }}
            withTip
          />
        )}
      </InputContainerWithIcon>
      {inputNote && <Note data-testid="text-input-note">{inputNote}</Note>}
      {!!errorMessage && (
        <ErrorMessage data-testid={`text-input-error-${inputKey}`}>
          <CautionIcon />
          {errorMessage}
        </ErrorMessage>
      )}
    </TextInputWrapper>
  );
};

export default TextInput;
